import { useEffect, useState } from 'react';
import { endpoints } from '../../../api';
import type { SpeechOrder } from '../../../api/speech';
import { parseTextResult } from './text-result-model';

/** JSON/TXT читаем только через защищённый download, с ограничением размера и времени. */
export function useTextResult(order: SpeechOrder | null, owner?: number) {
  const [loaded, setLoaded] = useState<{ identity: string; data: unknown } | null>(null);
  const [failure, setFailure] = useState('');
  const [revision, setRevision] = useState(0);
  const identity = `${owner}:${order?.id}:${order?.result_revision}`;
  const direct = parseTextResult(order?.result?.data ?? order?.result?.text);
  useEffect(() => {
    if (!owner || !order || order.status !== 'done' || !order.result?.available || parseTextResult(order.result.data)) return;
    const asset = order.result.assets.find(v => ['application/json', 'text/plain', 'text/json'].includes(v.mime));
    if (!asset) return;
    const controller = new AbortController();
    let active = true;
    const timer = setTimeout(() => {
      controller.abort();
      if (active) setFailure(identity);
    }, 30000);
    void (async () => {
      try {
        if ((await endpoints.me()).user.id !== owner || controller.signal.aborted) return;
        const blob = await endpoints.speech.result(order, asset.index, controller.signal);
        if (blob.size > 512 * 1024) throw Error('too_large');
        const text = await blob.text();
        const data: unknown = asset.mime === 'text/plain' ? { text } : JSON.parse(text);
        if (!parseTextResult(data)) throw Error('unsupported');
        if ((await endpoints.me()).user.id !== owner || controller.signal.aborted) return;
        setLoaded({ identity, data });
        setFailure('');
      } catch {
        if (!controller.signal.aborted) setFailure(identity);
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [order, owner, identity, revision]);
  return {
    document: direct ?? (loaded?.identity === identity ? parseTextResult(loaded.data) : null),
    error:
      failure === identity ||
      (!!order &&
        order.status === 'done' &&
        !!order.result?.available &&
        !direct &&
        !order.result.assets.some(v => ['application/json', 'text/plain', 'text/json'].includes(v.mime))),
    retry: () => setRevision(v => v + 1),
  };
}
